/**
 * Inline icon set.
 *
 * One icon per service plus the handful of status glyphs the widget needs.
 * Inline SVG rather than an icon font or a sprite because the widget is injected
 * into two unrelated pages: an inline `<svg>` cannot 404, cannot flash, cannot
 * collide with a host icon font, and needs no extra request. All of them are
 * `aria-hidden` — the accessible name always comes from real text.
 */

const ICONS: Record<string, string> = {
  /** Vakil — scales of justice. */
  scale:
    '<path d="M12 3v18M7 21h10M12 6l-7 2 3.5 6a3.5 3.5 0 0 1-7 0L5 8M12 6l7 2-3.5 6a3.5 3.5 0 0 0 7 0L19 8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  /** Morabi — dumbbell. */
  dumbbell:
    '<path d="M3 9v6M6 7v10M18 7v10M21 9v6M6 12h12" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>',
  /** Koodak — an adult and a child, hand in hand. */
  child:
    '<circle cx="8.5" cy="6.5" r="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8.5 9.5v6M8.5 15.5 6.4 21M8.5 15.5l2.1 5.5M5 12.5 8.5 11l3.5 1.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><circle cx="17.5" cy="10.5" r="2" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M17.5 13v3.6M17.5 16.6l-1.3 4.4M17.5 16.6l1.3 4.4M14.8 14.6 17.5 13.8l2.7.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
  lock:
    '<path d="M6 10V8a6 6 0 0 1 12 0v2M5 10h14v10H5z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  check:
    '<path d="M4 12.5l5 5L20 6.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
  alert:
    '<path d="M12 4l9 16H3zM12 10v4M12 17h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  refresh:
    '<path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  /** Empty state — an open archive box. */
  inbox:
    '<path d="M3.5 13.5 6 4.5h12l2.5 9M3.5 13.5V19a1 1 0 0 0 1 1h15a1 1 0 0 0 1-1v-5.5M3.5 13.5h5l1.2 2.2h4.6l1.2-2.2h5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  chevronDown:
    '<path d="M6 9.5 12 15.5l6-6" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>',
  /** Payment — a bank card. */
  card:
    '<rect x="3" y="5.5" width="18" height="13" rx="2.4" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3 10h18M6.5 14.5h4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>',
  /** Copy to clipboard — two stacked sheets. */
  copy:
    '<rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M15.5 8.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h2.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  /** Upload — an arrow out of a tray. */
  upload:
    '<path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4.5 14.5V19a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  close:
    '<path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  /** SMS / message bubble for the phone OTP login choice. */
  sms:
    '<path d="M4.5 5.5h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9.2L5.5 20.5V17.5h-1a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M8 10h8M8 13.5h5.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>',
  /**
   * Official multicolor Google “G” (inline paths; not an emoji).
   * Colours stay fixed so the mark remains recognizable on any host theme.
   */
  google:
    '<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>',
};

/** An inline SVG for a known icon key, or the generic alert glyph. */
export function iconSvg(name: string | null | undefined, size = 22): string {
  const path = ICONS[name ?? ''] ?? ICONS.alert!;
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" focusable="false">${path}</svg>`;
}

/** True when the icon key exists; used by tests to keep the catalog honest. */
export function hasIcon(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(ICONS, name);
}

export const ICON_NAMES = Object.keys(ICONS);
